import { ReactComponent as UsersIcon } from '../assets/icons/people_alt-icon.svg';
import { ReactComponent as AttributesIcon } from '../assets/icons/list_alt.svg';
import { ReactComponent as StatusIcon } from '../assets/icons/status-icon.svg';
import { ReactComponent as CreatorIcon } from '../assets/icons/creator-icon.svg';
import { ReactComponent as ReminderIcon } from '../assets/icons/notifications_none.svg';
import { ReactComponent as SurveyTitleIcon } from '../assets/icons/title.svg';
import { ReactComponent as RepeatIcon } from '../assets/icons/repeat.svg';
import { API_USERS_BASIC } from '../constants/apiRoutes';
import { PARAMS } from '../constants/pages';
import { SYSTEM_ROLES } from '../constants/roles';
import {
  CYCLES_STATUSES_LIST,
  CYCLE_STATUSES,
  SURVEY_STATUS_TYPES,
  USER_STATUSES,
} from '../constants/statuses';
import { PEOPLE_DEFAULT_ORDERING } from '../constants/people';
import {
  ASSIGNMENT_FIELDS,
  CYCLE_STEPS_MAP,
  CYCLE_STEP_TYPES,
  MANAGE_CYCLE_FIELD_NAMES,
  STEP_GLOBAL_FIELDS,
  EMAIL_STEP_FIELD_NAMES,
  REPEAT_INTERVAL_OPTIONS,
  INTERVAL_UNIT_TYPES,
  CYCLE_REVIEW_TYPES_MAP,
} from '../constants/cycle';
import { FIELD_TYPES } from '../constants/fieldTypes';
import { isEmpty, forbiddenCharacters, validateLength } from './validation';
import http from './http';
import {
  prepareFilterableUsers,
  sortPeopleAlphabetically,
  parseDuplicateParameters,
} from './uiUtils';
import { prepareSurveyStatuses } from './survey';
import {
  getItemById,
  getObjectToNumberArray,
  getUnionOfTwoArrays,
  isArrayEmpty,
  isValueInList,
  getItemIndex,
  calculatePercentageToNDecimals,
  getArrayDifference,
} from './helpers';
import { isUserProfileAccessibleCheck } from './permissions';
import {
  formatDate,
  isDateInPast,
  addDaysFromSpecificHour,
  checkIsDateAfterPrevious,
  addNumberOfDays,
  isDateInFuture,
  getDifferenceInDays,
} from './dateUtils';

export const prepareCycleReviewTypes = labels =>
  CYCLE_REVIEW_TYPES_MAP.map(type => ({
    ...type,
    ...labels?.[type.key],
  }));

const prepareTableTimeline = (labels, startDate, endDate, isCompleted) => {
  const isStartInFuture = isDateInFuture(startDate);
  const isEndPast = isDateInPast(endDate);

  if (isStartInFuture) {
    const daysToStart = getDifferenceInDays(startDate);

    if (daysToStart === 0) {
      return labels.startsToday;
    }

    return `${labels.start} ${daysToStart} ${
      daysToStart > 1 ? labels.days : labels.day
    }`;
  }

  if (!isEndPast && !isCompleted) {
    const daysToEnd = getDifferenceInDays(endDate);

    if (daysToEnd === 0) {
      return labels.endsToday;
    }

    return `${labels.end} ${daysToEnd} ${
      daysToEnd > 1 ? labels.days : labels.day
    }`;
  }

  return labels.ended;
};

const removeExludedUsers = users => users.filter(user => !user.isExcluded);

const prepareStepParticipants = (stepType, users, existingAssignments = []) => {
  const { SELF_REVIEW_STEP, TOP_DOWN_STEP } = CYCLE_STEP_TYPES;
  const { REVIEWERS, CREATED_FOR } = ASSIGNMENT_FIELDS;

  if (isArrayEmpty(users)) return [];

  if (stepType === SELF_REVIEW_STEP) {
    const [existingAssignment] = existingAssignments;

    if (existingAssignment) {
      const existingReviewers = existingAssignment[REVIEWERS];

      const reviewers = users.reduce((acc, user) => {
        const existingReviewer = getItemById(existingReviewers, user.id);

        return [
          ...acc,
          ...(existingReviewer ? [{ ...existingReviewer }] : [{ ...user }]),
        ];
      }, []);

      return [{ reviewers, [CREATED_FOR]: [] }];
    }

    return [{ [REVIEWERS]: [...users], [CREATED_FOR]: [] }];
  }

  return users.reduce((acc, user) => {
    if (stepType === TOP_DOWN_STEP) {
      const existingAssignment = existingAssignments?.find(assignment =>
        assignment?.[REVIEWERS]?.find(reviewer => reviewer.id === user.id)
      );
      const existingReviewer = existingAssignment?.[REVIEWERS]?.find(
        reviewer => reviewer.id === user.id
      );
      const reporters = users.reduce((reportersAcc, reporter) => {
        const existingReporter = existingAssignment?.[CREATED_FOR]?.find(
          currRep => currRep.id === reporter.id
        );

        if (existingReporter) {
          return [...reportersAcc, { ...existingReporter }];
        }

        return [
          ...reportersAcc,
          ...(reporter.report_to === user.id ? [{ ...reporter }] : []),
        ];
      }, []);

      if (isArrayEmpty(reporters)) return acc;

      return [
        ...acc,
        {
          ...(acc[REVIEWERS]
            ? {
                [REVIEWERS]: [
                  ...acc.reviewers,
                  ...(existingReviewer
                    ? [{ ...existingReviewer }]
                    : [{ ...user }]),
                ],
                [CREATED_FOR]: reporters,
              }
            : {
                [REVIEWERS]: [
                  ...(existingReviewer
                    ? [{ ...existingReviewer }]
                    : [{ ...user }]),
                ],
                [CREATED_FOR]: reporters,
              }),
        },
      ];
    }

    const existingAssignment = existingAssignments?.find(assignment =>
      assignment?.[CREATED_FOR]?.find(createdFor => createdFor.id === user.id)
    );
    const existingCreatedFor = existingAssignment?.[CREATED_FOR]?.find(
      createdFor => createdFor.id === user.id
    );
    const reporters = users.reduce((reportersAcc, reporter) => {
      const existingReporter = existingAssignment?.[REVIEWERS]?.find(
        currRep => currRep.id === reporter.id
      );

      if (existingReporter) {
        return [...reportersAcc, { ...existingReporter }];
      }

      return [
        ...reportersAcc,
        ...(reporter.report_to === user.id ? [{ ...reporter }] : []),
      ];
    }, []);

    if (isArrayEmpty(reporters)) return acc;

    return [
      ...acc,
      {
        [CREATED_FOR]: [
          ...(existingCreatedFor ? [{ ...existingCreatedFor }] : [{ ...user }]),
        ],
        [REVIEWERS]: reporters,
      },
    ];
  }, []);
};

const prepareStepsInitialData = (translations, steps, existingSteps = []) => {
  const { EMAIL_STEP, SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } =
    CYCLE_STEP_TYPES;
  const { ASSIGNMENTS, START_AT, END_AT, HAS_EMAIL, ATTRIBUTES } =
    STEP_GLOBAL_FIELDS;
  const { EMAIL_SUBJECT, EMAIL_MESSAGE } = EMAIL_STEP_FIELD_NAMES;

  return steps.map(step => {
    if (step.id === EMAIL_STEP) {
      return {
        ...step,
        initialData: {
          [EMAIL_SUBJECT]: '',
          [EMAIL_MESSAGE]: '',
          [START_AT]: addDaysFromSpecificHour(1),
        },
        isActive: !!getItemById(existingSteps, EMAIL_STEP, 'type'),
      };
    }
    if (step.id === SELF_REVIEW_STEP) {
      return {
        ...step,
        initialData: {
          [HAS_EMAIL]: false,
          [EMAIL_MESSAGE]: translations.defaultStepEmailMessage,
          [ATTRIBUTES]: [],
          [ASSIGNMENTS]: [],
          [START_AT]: addDaysFromSpecificHour(2),
          [END_AT]: addDaysFromSpecificHour(4),
        },
        isActive: !!getItemById(existingSteps, SELF_REVIEW_STEP, 'type'),
      };
    }
    if (step.id === TOP_DOWN_STEP) {
      return {
        ...step,
        initialData: {
          [HAS_EMAIL]: false,
          [EMAIL_MESSAGE]: translations.defaultStepEmailMessage,
          [ATTRIBUTES]: [],
          [ASSIGNMENTS]: [],
          [START_AT]: addDaysFromSpecificHour(2),
          [END_AT]: addDaysFromSpecificHour(4),
        },
        isActive: !!getItemById(existingSteps, TOP_DOWN_STEP, 'type'),
      };
    }
    if (step.id === DOWN_UP_STEP) {
      return {
        ...step,
        initialData: {
          [HAS_EMAIL]: false,
          [EMAIL_MESSAGE]: translations.defaultStepEmailMessage,
          [ATTRIBUTES]: [],
          [ASSIGNMENTS]: [],
          [START_AT]: addDaysFromSpecificHour(2),
          [END_AT]: addDaysFromSpecificHour(4),
        },
        isActive: !!getItemById(existingSteps, DOWN_UP_STEP, 'type'),
      };
    }
    return step;
  });
};

const prepareEditStepsData = (steps, users) => {
  const { EMAIL_STEP, SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } =
    CYCLE_STEP_TYPES;
  const { ASSIGNMENTS, START_AT, END_AT, HAS_EMAIL, ATTRIBUTES } =
    STEP_GLOBAL_FIELDS;
  const { EMAIL_SUBJECT, EMAIL_MESSAGE } = EMAIL_STEP_FIELD_NAMES;
  const { REVIEWERS, CREATED_FOR } = ASSIGNMENT_FIELDS;

  return steps.reduce(
    (stepsAcc, step) => {
      if (step.type === EMAIL_STEP) {
        return {
          ...stepsAcc,
          [EMAIL_STEP]: {
            id: step.id,
            [EMAIL_SUBJECT]: step[EMAIL_SUBJECT],
            [EMAIL_MESSAGE]: step[EMAIL_MESSAGE],
            [START_AT]: new Date(step[START_AT]),
          },
        };
      }
      if (step.type === SELF_REVIEW_STEP) {
        const reviewers = users.reduce((reviewersAcc, reviewer) => {
          const isReviewerInList = isValueInList(
            step[ASSIGNMENTS][0][REVIEWERS],
            reviewer.id
          );
          if (isReviewerInList) {
            return [...reviewersAcc, { ...reviewer }];
          }
          return [...reviewersAcc, { ...reviewer, isExcluded: true }];
        }, []);

        return {
          ...stepsAcc,
          [SELF_REVIEW_STEP]: {
            id: step.id,
            [HAS_EMAIL]: false,
            [EMAIL_MESSAGE]: step[EMAIL_MESSAGE],
            [ATTRIBUTES]: step[ATTRIBUTES],
            [START_AT]: new Date(step[START_AT]),
            [END_AT]: new Date(step[END_AT]),
            [ASSIGNMENTS]: [
              {
                [REVIEWERS]: sortPeopleAlphabetically(
                  [...reviewers],
                  'isExcluded',
                  true
                ),
                [CREATED_FOR]: [],
              },
            ],
          },
        };
      }
      if (step.type === TOP_DOWN_STEP) {
        return {
          ...stepsAcc,
          [TOP_DOWN_STEP]: {
            id: step.id,
            [HAS_EMAIL]: false,
            [EMAIL_MESSAGE]: step[EMAIL_MESSAGE],
            [ATTRIBUTES]: step[ATTRIBUTES],
            [START_AT]: new Date(step[START_AT]),
            [END_AT]: new Date(step[END_AT]),
            [ASSIGNMENTS]: users.reduce((acc, user) => {
              const existingAssignment = step[ASSIGNMENTS]?.find(assignment =>
                assignment?.[REVIEWERS]?.find(reviewer => reviewer === user.id)
              );
              const reporters = users.filter(
                reporter => reporter.report_to === user.id
              );

              if (existingAssignment) {
                return [
                  ...acc,
                  {
                    [REVIEWERS]: [{ ...user }],
                    [CREATED_FOR]: sortPeopleAlphabetically(
                      [
                        ...reporters.map(reporter => {
                          const isReporterActive = isValueInList(
                            existingAssignment[CREATED_FOR],
                            reporter.id
                          );
                          if (isReporterActive) {
                            return { ...reporter };
                          }
                          return { ...reporter, isExcluded: true };
                        }),
                      ],
                      'isExcluded',
                      true
                    ),
                  },
                ];
              }

              if (!isArrayEmpty(reporters)) {
                return [
                  ...acc,
                  {
                    [REVIEWERS]: [{ ...user, isExcluded: true }],
                    [CREATED_FOR]: sortPeopleAlphabetically(
                      [
                        ...reporters.map(reporter => ({
                          ...reporter,
                          isExcluded: true,
                        })),
                      ],
                      'isExcluded',
                      true
                    ),
                  },
                ];
              }

              return acc;
            }, []),
          },
        };
      }

      return {
        ...stepsAcc,
        [DOWN_UP_STEP]: {
          id: step.id,
          [HAS_EMAIL]: false,
          [EMAIL_MESSAGE]: step[EMAIL_MESSAGE],
          [ATTRIBUTES]: step[ATTRIBUTES],
          [START_AT]: new Date(step[START_AT]),
          [END_AT]: new Date(step[END_AT]),
          [ASSIGNMENTS]: users.reduce((acc, user) => {
            const existingAssignment = step[ASSIGNMENTS]?.find(assignment =>
              assignment?.[CREATED_FOR]?.find(
                createdFor => createdFor === user.id
              )
            );
            const reporters = users.filter(
              reporter => reporter.report_to === user.id
            );

            if (existingAssignment) {
              return [
                ...acc,
                {
                  [REVIEWERS]: sortPeopleAlphabetically(
                    [
                      ...reporters.map(reporter => {
                        const isReporterActive = isValueInList(
                          existingAssignment[REVIEWERS],
                          reporter.id
                        );
                        if (isReporterActive) {
                          return { ...reporter };
                        }
                        return { ...reporter, isExcluded: true };
                      }),
                    ],
                    'isExcluded',
                    true
                  ),
                  [CREATED_FOR]: [{ ...user }],
                },
              ];
            }

            if (!isArrayEmpty(reporters)) {
              return [
                ...acc,
                {
                  [REVIEWERS]: sortPeopleAlphabetically(
                    [
                      ...reporters.map(reporter => ({
                        ...reporter,
                        isExcluded: true,
                      })),
                    ],
                    'isExcluded',
                    true
                  ),
                  [CREATED_FOR]: [{ ...user, isExcluded: true }],
                },
              ];
            }

            return acc;
          }, []),
        },
      };
    },
    {
      [EMAIL_STEP]: null,
      [SELF_REVIEW_STEP]: null,
      [TOP_DOWN_STEP]: null,
      [DOWN_UP_STEP]: null,
    }
  );
};

const prepareAssignmentsForSave = (assignments, isSelfReviewStep = false) => {
  const { REVIEWERS, CREATED_FOR } = ASSIGNMENT_FIELDS;

  return assignments.reduce((acc, currentAssignment) => {
    const createdFor = removeExludedUsers(currentAssignment[CREATED_FOR]);
    const reviewers = removeExludedUsers(currentAssignment[REVIEWERS]);

    if (
      (isArrayEmpty(createdFor) && !isSelfReviewStep) ||
      isArrayEmpty(reviewers)
    ) {
      return acc;
    }

    return [
      ...acc,
      {
        [CREATED_FOR]: getObjectToNumberArray(createdFor),
        [REVIEWERS]: getObjectToNumberArray(reviewers),
      },
    ];
  }, []);
};

const prepareEmailStepUsersForNotifications = users => {
  const { REVIEWERS } = ASSIGNMENT_FIELDS;
  const usersToNotify = removeExludedUsers(getUnionOfTwoArrays(users, []));

  return [
    {
      [REVIEWERS]: getObjectToNumberArray(usersToNotify),
    },
  ];
};

const getParticipantsCount = (stepType, assignments) => {
  const { SELF_REVIEW_STEP } = CYCLE_STEP_TYPES;
  const { REVIEWERS, CREATED_FOR } = ASSIGNMENT_FIELDS;

  if (stepType === SELF_REVIEW_STEP) {
    return assignments.reduce((count, currAssignment) => {
      return count + currAssignment[REVIEWERS].length;
    }, 0);
  }

  const allParticipants = assignments.reduce(
    (acc, currAssignment) => [
      ...acc,
      ...currAssignment[REVIEWERS],
      ...currAssignment[CREATED_FOR],
    ],
    []
  );
  const uniqueParticipants = getUnionOfTwoArrays(allParticipants, []);

  return uniqueParticipants.length;
};

const prepareAttributesWithAnswerComments = attributes =>
  attributes?.reduce((acc, curr) => {
    return {
      ...acc,
      [curr.id]: curr.has_answer_comment,
    };
  }, {});

export const prepareStepForSave = (stepType, stepValues, cycleValues = {}) => {
  const { EMAIL_STEP, SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } =
    CYCLE_STEP_TYPES;
  const { ASSIGNMENTS, START_AT, END_AT, ATTRIBUTES, ATTRIBUTES_WITH_COMMENT } =
    STEP_GLOBAL_FIELDS;
  const { EMAIL_SUBJECT, EMAIL_MESSAGE } = EMAIL_STEP_FIELD_NAMES;
  const { REVIEWERS } = ASSIGNMENT_FIELDS;

  if (stepType === EMAIL_STEP) {
    return {
      type: stepType,
      [ASSIGNMENTS]: prepareEmailStepUsersForNotifications([
        ...(cycleValues[SELF_REVIEW_STEP]
          ? cycleValues[SELF_REVIEW_STEP][ASSIGNMENTS][0][REVIEWERS]
          : []),
        ...(cycleValues[TOP_DOWN_STEP]
          ? cycleValues[TOP_DOWN_STEP][ASSIGNMENTS].reduce(
              (acc, curr) => [...acc, ...curr.reviewers],
              []
            )
          : []),
        ...(cycleValues[DOWN_UP_STEP]
          ? cycleValues[DOWN_UP_STEP][ASSIGNMENTS].reduce(
              (acc, curr) => [...acc, ...curr.reviewers],
              []
            )
          : []),
      ]),
      [START_AT]: stepValues[START_AT],
      [END_AT]: stepValues[START_AT],
      [EMAIL_SUBJECT]: stepValues[EMAIL_SUBJECT],
      [EMAIL_MESSAGE]: stepValues[EMAIL_MESSAGE],
    };
  }

  return {
    type: stepType,
    [ASSIGNMENTS]: prepareAssignmentsForSave(
      stepValues[ASSIGNMENTS],
      stepType === SELF_REVIEW_STEP
    ),
    [ATTRIBUTES]: getObjectToNumberArray(stepValues[ATTRIBUTES]),
    [ATTRIBUTES_WITH_COMMENT]: prepareAttributesWithAnswerComments(
      stepValues[ATTRIBUTES]
    ),
    [START_AT]: stepValues[START_AT],
    [END_AT]: stepValues[END_AT],
    [EMAIL_MESSAGE]: stepValues[EMAIL_MESSAGE],
  };
};

export const prepareCycleSteps = labels =>
  CYCLE_STEPS_MAP.map(step => ({
    ...step,
    name: labels[step.key].name,
    shortName: labels[step.key].shortName,
  }));

export const prepareIntervalOptions = labels => {
  return REPEAT_INTERVAL_OPTIONS.map(interval => {
    return {
      ...interval,
      name: labels[interval.key].name,
      periods: interval.periods.map((intervalOption, index) => {
        return {
          ...intervalOption,
          name: `${intervalOption.name} ${
            index === 0
              ? labels[interval.key].single
              : labels[interval.key].plural
          }`,
        };
      }),
    };
  });
};

export const prepareCycleInitialData = (translations, steps, users, cycle) => {
  const {
    TITLE,
    CYCLE_STEPS,
    IS_REPEATING,
    REPEAT_INTERVAL_UNIT,
    REPEAT_INTERVAL,
    ACTIVE_SETTINGS,
    USERS,
    FILTERS,
  } = MANAGE_CYCLE_FIELD_NAMES;
  const { EMAIL_STEP, SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } =
    CYCLE_STEP_TYPES;
  const {
    ID,
    ROLE,
    STATUS,
    SCOPE,
    JOB_TITLE,
    TRACK,
    LEVEL,
    DIVISION,
    DEPARTMENT,
    TAGS,
  } = PARAMS;
  const initialSteps = prepareStepsInitialData(
    translations,
    steps,
    cycle?.steps
  );

  if (cycle) {
    const cycleUsers = users.filter(user =>
      isValueInList(cycle[USERS], user.id)
    );

    return {
      id: cycle.id,
      [ACTIVE_SETTINGS]: {
        main: USERS,
        sub: null,
      },
      [TITLE]: cycle[TITLE],
      [CYCLE_STEPS]: initialSteps,
      [USERS]: cycleUsers,
      [IS_REPEATING]: !!cycle?.[IS_REPEATING],
      [REPEAT_INTERVAL_UNIT]: cycle?.[REPEAT_INTERVAL_UNIT],
      [REPEAT_INTERVAL]: cycle?.[REPEAT_INTERVAL] || 1,
      [FILTERS]: {
        selected: {
          [ID]: [],
          [ROLE]: [],
          [STATUS]: [],
          [SCOPE]: [],
          [JOB_TITLE]: [],
          [TRACK]: [],
          [LEVEL]: [],
          [DIVISION]: [],
          [DEPARTMENT]: [],
          [TAGS]: [],
        },
        isDisabled: true,
      },
      ...prepareEditStepsData(cycle.steps, cycleUsers),
    };
  }

  return {
    [ACTIVE_SETTINGS]: {
      main: USERS,
      sub: null,
    },
    [TITLE]: '',
    [CYCLE_STEPS]: initialSteps,
    [EMAIL_STEP]: null,
    [SELF_REVIEW_STEP]: null,
    [TOP_DOWN_STEP]: null,
    [DOWN_UP_STEP]: null,
    [USERS]: users,
    [IS_REPEATING]: false,
    [REPEAT_INTERVAL_UNIT]: INTERVAL_UNIT_TYPES.MONTH,
    [REPEAT_INTERVAL]: 1,
    [FILTERS]: {
      selected: {
        [ID]: [],
        [ROLE]: [],
        [STATUS]: [],
        [SCOPE]: [],
        [JOB_TITLE]: [],
        [TRACK]: [],
        [LEVEL]: [],
        [DIVISION]: [],
        [DEPARTMENT]: [],
        [TAGS]: [],
      },
      isDisabled: false,
    },
  };
};

export const prepareManageCycleFormColumns = (
  classes,
  activeSteps,
  peopleFilters,
  intervalOptions,
  setActiveSteps,
  onAttributePreview
) => {
  const {
    TEXT,
    SETTINGS_SUMMARY,
    SWITCH,
    DATE_PICKER,
    TIME_PICKER,
    TOGGLEABLE_SETTINGS,
    SUBTITLE,
    COLLAPSABLE_FILTERS,
    MANAGE_USERS_LIST,
    SELECT,
    CYCLE_STEP_ASSIGNMENTS,
  } = FIELD_TYPES;
  const {
    TITLE,
    CYCLE_STEPS,
    ACTIVE_SETTINGS,
    IS_REPEATING,
    REPEAT_INTERVAL,
    REPEAT_INTERVAL_UNIT,
    USERS,
    FILTERS,
  } = MANAGE_CYCLE_FIELD_NAMES;
  const { EMAIL_STEP, SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } =
    CYCLE_STEP_TYPES;
  const { ATTRIBUTES, ASSIGNMENTS, HAS_EMAIL, START_AT, END_AT } =
    STEP_GLOBAL_FIELDS;
  const { EMAIL_SUBJECT, EMAIL_MESSAGE } = EMAIL_STEP_FIELD_NAMES;
  const { REVIEWERS, CREATED_FOR } = ASSIGNMENT_FIELDS;

  return [
    {
      id: 1,
      columnContentWrapperClass: 'columnContent',
      fields: [
        {
          name: TITLE,
          type: TEXT,
          fieldWrapperClass: classes.name,
          required: true,
          hasDivider: true,
          validators: [
            { type: 'required', validator: isEmpty },
            { type: 'forbiddenCharacters', validator: forbiddenCharacters },
            {
              type: 'maxLength',
              validator: value => validateLength(value, 0, 150),
            },
          ],
        },
        {
          name: ACTIVE_SETTINGS,
          type: SETTINGS_SUMMARY,
          translationKey: 'usersSettings',
          fieldWrapperClass: classes.usersSettings,
          icon: UsersIcon,
          settingsKey: USERS,
          hasDivider: true,
          isSubSetting: false,
          prepareValueCount: value => value.length,
        },
        {
          name: CYCLE_STEPS,
          type: FIELD_TYPES.CYCLE_STEPS,
          translationKey: 'cycleSteps',
          fieldWrapperClass: classes.cycleSteps,
          hasDivider: true,
          settingsKey: ACTIVE_SETTINGS,
          dependants: [
            {
              name: ACTIVE_SETTINGS,
              manualResetFn: ({
                values,
                errors,
                currentFieldValue,
                dependantValue,
                setValues,
                setErrors,
              }) => {
                if (currentFieldValue.isActive) {
                  setActiveSteps(prevSteps => ({
                    ...prevSteps,
                    [currentFieldValue.id]: currentFieldValue.isActive,
                  }));
                  setValues(
                    {
                      ...values,
                      [ACTIVE_SETTINGS]: {
                        ...dependantValue,
                        main: currentFieldValue.id,
                        sub: null,
                      },
                      [currentFieldValue.id]:
                        currentFieldValue.id === EMAIL_STEP
                          ? currentFieldValue.initialData
                          : {
                              ...currentFieldValue.initialData,
                              [ASSIGNMENTS]: prepareStepParticipants(
                                currentFieldValue.id,
                                values[USERS]
                              ),
                            },
                    },
                    false
                  );
                } else {
                  setValues(
                    {
                      ...values,
                      [ACTIVE_SETTINGS]: {
                        ...dependantValue,
                        main: USERS,
                        sub: null,
                      },
                      [currentFieldValue.id]: null,
                    },
                    false
                  );
                  setErrors({
                    ...errors,
                    [currentFieldValue.id]: null,
                    // Reset review step date errors if email step is deactivated
                    ...(currentFieldValue.id === EMAIL_STEP &&
                    errors?.[SELF_REVIEW_STEP]?.[START_AT] ===
                      'stepStartDateInvalid'
                      ? {
                          [SELF_REVIEW_STEP]: {
                            ...errors[SELF_REVIEW_STEP],
                            [START_AT]: null,
                          },
                        }
                      : {}),
                    ...(currentFieldValue.id === EMAIL_STEP &&
                    errors?.[TOP_DOWN_STEP]?.[START_AT] ===
                      'stepStartDateInvalid'
                      ? {
                          [TOP_DOWN_STEP]: {
                            ...errors[TOP_DOWN_STEP],
                            [START_AT]: null,
                          },
                        }
                      : {}),
                    ...(currentFieldValue.id === EMAIL_STEP &&
                    errors?.[DOWN_UP_STEP]?.[START_AT] ===
                      'stepStartDateInvalid'
                      ? {
                          [DOWN_UP_STEP]: {
                            ...errors[DOWN_UP_STEP],
                            [START_AT]: null,
                          },
                        }
                      : {}),
                    // Reset email step date errors if all review steps are deactivated
                    ...(errors?.[EMAIL_STEP]?.[START_AT] ===
                      'emailStepDateInvalid' &&
                    currentFieldValue.id === SELF_REVIEW_STEP &&
                    values[TOP_DOWN_STEP] === null &&
                    values[DOWN_UP_STEP] === null
                      ? {
                          [EMAIL_STEP]: {
                            ...errors[EMAIL_STEP],
                            [START_AT]: null,
                          },
                        }
                      : {}),
                    ...(errors?.[EMAIL_STEP]?.[START_AT] ===
                      'emailStepDateInvalid' &&
                    currentFieldValue.id === TOP_DOWN_STEP &&
                    values[SELF_REVIEW_STEP] === null &&
                    values[DOWN_UP_STEP] === null
                      ? {
                          [EMAIL_STEP]: {
                            ...errors[EMAIL_STEP],
                            [START_AT]: null,
                          },
                        }
                      : {}),
                    ...(errors?.[EMAIL_STEP]?.[START_AT] ===
                      'emailStepDateInvalid' &&
                    currentFieldValue.id === DOWN_UP_STEP &&
                    values[TOP_DOWN_STEP] === null &&
                    values[SELF_REVIEW_STEP] === null
                      ? {
                          [EMAIL_STEP]: {
                            ...errors[EMAIL_STEP],
                            [START_AT]: null,
                          },
                        }
                      : {}),
                  });
                  setActiveSteps(prevSteps => ({
                    ...prevSteps,
                    [currentFieldValue.id]: false,
                  }));
                }
              },
            },
          ],
          validators: [
            {
              type: 'steps',
              validator: steps =>
                steps.some(step => step.isActive && step.id !== EMAIL_STEP),
            },
          ],
        },
        {
          name: ACTIVE_SETTINGS,
          type: TOGGLEABLE_SETTINGS,
          translationKey: 'repeatingCycle',
          settingsFor: IS_REPEATING,
          icon: RepeatIcon,
          prepareInfo: values => {
            const selectedRepeat = getItemById(
              intervalOptions,
              values[REPEAT_INTERVAL_UNIT]
            );

            return values[IS_REPEATING]
              ? getItemById(selectedRepeat.periods, values[REPEAT_INTERVAL])
                  .name
              : '';
          },
          dependants: [
            {
              name: ACTIVE_SETTINGS,
              resetFnc: (value, curentValue, dependantValue) => {
                if (curentValue) {
                  return { ...dependantValue, main: IS_REPEATING, sub: null };
                }

                return { ...dependantValue, main: USERS, sub: null };
              },
            },
            {
              name: REPEAT_INTERVAL_UNIT,
              resetFnc: (value, curentValue, dependantValue, initialValues) => {
                if (curentValue) {
                  return dependantValue;
                }

                return initialValues[REPEAT_INTERVAL_UNIT];
              },
            },
            {
              name: REPEAT_INTERVAL,
              resetFnc: (value, curentValue, dependantValue, initialValues) => {
                if (curentValue) {
                  return dependantValue;
                }

                return initialValues[REPEAT_INTERVAL];
              },
            },
          ],
        },
      ],
    },
    {
      id: 2,
      columnContentWrapperClass: 'secondarySettingsColumnContent',
      columnClass: classes.secondarySettingsColumn,
      fields: [
        // REPEAT FIELDS
        {
          name: 'repeatTitle',
          type: SUBTITLE,
          fieldWrapperClass: classes.repeatTitle,
          isToggleable: true,
          shouldFadeOutInstant: true,
          variant: 'h4',
          shouldRender: values =>
            values?.[ACTIVE_SETTINGS]?.main === IS_REPEATING &&
            values?.[IS_REPEATING],
        },
        {
          name: 'repeatDescription',
          type: SUBTITLE,
          fieldWrapperClass: classes.repeatDescription,
          isToggleable: true,
          shouldFadeOutInstant: true,
          variant: 'body2',
          shouldRender: values =>
            values?.[ACTIVE_SETTINGS]?.main === IS_REPEATING &&
            values?.[IS_REPEATING],
        },
        {
          name: REPEAT_INTERVAL_UNIT,
          type: SELECT,
          translationKey: 'repeatIntervalUnit',
          className: classes.intervalUnitField,
          fieldWrapperClass: classes.intervalUnit,
          optionsKey: 'intervalOptions',
          shouldDisableSort: true,
          isToggleable: true,
          shouldFadeOutInstant: true,
          isSearchDisabled: true,
          shouldRender: values =>
            values?.[ACTIVE_SETTINGS]?.main === IS_REPEATING &&
            values?.[IS_REPEATING],
          parser: {
            value: 'id',
            label: 'name',
          },
          dependants: [
            {
              name: REPEAT_INTERVAL,
              value: 1,
            },
          ],
        },
        {
          name: REPEAT_INTERVAL,
          type: SELECT,
          translationKey: 'repeatInterval',
          fieldWrapperClass: classes.intervalPeriod,
          className: classes.intervalPeriodField,
          optionsKey: 'periods',
          shouldDisableSort: true,
          isToggleable: true,
          shouldFadeOutInstant: true,
          isSearchDisabled: true,
          shouldRender: values =>
            values?.[ACTIVE_SETTINGS]?.main === IS_REPEATING &&
            values?.[IS_REPEATING],
          parent: {
            name: REPEAT_INTERVAL_UNIT,
            options: 'intervalOptions',
          },
          parser: {
            value: 'id',
            label: 'name',
          },
        },
        // USER FIELDS
        {
          name: FILTERS,
          type: COLLAPSABLE_FILTERS,
          fieldWrapperClass: classes.users,
          isToggleable: true,
          shouldFadeOutInstant: true,
          parentOffset: 24,
          shouldRemoveFieldSpacing: true,
          shouldRemoveFieldError: true,
          isExternalyManagedValue: true,
          filters: peopleFilters,
          getResults: async selectedFilters => {
            const { data } = await http.get(API_USERS_BASIC, {
              params: {
                ...selectedFilters,
                ...PEOPLE_DEFAULT_ORDERING,
                [PARAMS.EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
                [PARAMS.EXCLUDE_STATUS]: USER_STATUSES.DEACTIVATED,
              },
              paramsSerializer: d => parseDuplicateParameters(d),
            });

            return data;
          },
          dependants: [
            {
              name: 'reviewers',
              manualResetFn: ({
                values,
                errors,
                currentFieldValue,
                setValues,
                setErrors,
              }) => {
                setValues({
                  ...values,
                  [USERS]: currentFieldValue?.filteredValues,
                  [FILTERS]: {
                    selected: currentFieldValue.selected,
                    isDisabled: currentFieldValue.isDisabled,
                  },
                  ...(values[SELF_REVIEW_STEP]
                    ? {
                        [SELF_REVIEW_STEP]: {
                          ...values[SELF_REVIEW_STEP],
                          [ASSIGNMENTS]: prepareStepParticipants(
                            SELF_REVIEW_STEP,
                            currentFieldValue?.filteredValues,
                            values[SELF_REVIEW_STEP][ASSIGNMENTS]
                          ),
                        },
                      }
                    : {}),
                  ...(values[TOP_DOWN_STEP]
                    ? {
                        [TOP_DOWN_STEP]: {
                          ...values[TOP_DOWN_STEP],
                          [ASSIGNMENTS]: prepareStepParticipants(
                            TOP_DOWN_STEP,
                            currentFieldValue?.filteredValues,
                            values[TOP_DOWN_STEP][ASSIGNMENTS]
                          ),
                        },
                      }
                    : {}),
                  ...(values[DOWN_UP_STEP]
                    ? {
                        [DOWN_UP_STEP]: {
                          ...values[DOWN_UP_STEP],
                          [ASSIGNMENTS]: prepareStepParticipants(
                            DOWN_UP_STEP,
                            currentFieldValue?.filteredValues,
                            values[DOWN_UP_STEP][ASSIGNMENTS]
                          ),
                        },
                      }
                    : {}),
                });
                setErrors({
                  ...errors,
                  ...(errors[SELF_REVIEW_STEP]
                    ? {
                        [SELF_REVIEW_STEP]: {
                          ...errors[SELF_REVIEW_STEP],
                          [ASSIGNMENTS]: null,
                        },
                      }
                    : {}),
                  ...(errors[TOP_DOWN_STEP]
                    ? {
                        [TOP_DOWN_STEP]: {
                          ...errors[TOP_DOWN_STEP],
                          [ASSIGNMENTS]: null,
                        },
                      }
                    : {}),
                  ...(errors[DOWN_UP_STEP]
                    ? {
                        [DOWN_UP_STEP]: {
                          ...errors[DOWN_UP_STEP],
                          [ASSIGNMENTS]: null,
                        },
                      }
                    : {}),
                });
              },
            },
          ],
          shouldRender: values => values?.[ACTIVE_SETTINGS]?.main === USERS,
          validators: [
            { type: 'required', validator: value => !isArrayEmpty(value) },
          ],
        },
        {
          name: 'disabledFilter',
          type: SUBTITLE,
          fieldWrapperClass: classes.disabledFilter,
          isToggleable: true,
          shouldFadeOutInstant: true,
          variant: 'body2',
          shouldRender: values =>
            values?.[ACTIVE_SETTINGS]?.main === USERS &&
            values[FILTERS]?.isDisabled,
        },
        // EMAIL STEP FIELDS
        ...(activeSteps[EMAIL_STEP]
          ? [
              {
                name: 'emailStepSubtitle',
                type: SUBTITLE,
                fieldWrapperClass: classes.subtitle,
                isToggleable: true,
                shouldFadeOutInstant: true,
                variant: 'h4',
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === EMAIL_STEP,
              },
            ]
          : []),
        ...(activeSteps[EMAIL_STEP]
          ? [
              {
                name: START_AT,
                type: DATE_PICKER,
                fieldWrapperClass: classes.startDate,
                translationKey: 'stepStartDate',
                isToolbarDisabled: true,
                isPastDisabled: true,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: EMAIL_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === EMAIL_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: startDate => !isDateInPast(startDate),
                  },
                  {
                    type: 'emailStepDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[SELF_REVIEW_STEP]) {
                        isValid = !checkIsDateAfterPrevious(
                          startDate,
                          values[SELF_REVIEW_STEP][START_AT]
                        );
                      }
                      if (values[TOP_DOWN_STEP]) {
                        isValid = !checkIsDateAfterPrevious(
                          startDate,
                          values[TOP_DOWN_STEP][START_AT]
                        );
                      }
                      if (values[DOWN_UP_STEP]) {
                        isValid = !checkIsDateAfterPrevious(
                          startDate,
                          values[DOWN_UP_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[EMAIL_STEP]
          ? [
              {
                name: START_AT,
                type: TIME_PICKER,
                fieldWrapperClass: classes.startTime,
                isToggleable: true,
                shouldHideLabel: true,
                shouldShowError: false,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: EMAIL_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === EMAIL_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: startDate => !isDateInPast(startDate),
                  },
                  {
                    type: 'emailStepDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[SELF_REVIEW_STEP]) {
                        isValid = !checkIsDateAfterPrevious(
                          startDate,
                          values[SELF_REVIEW_STEP][START_AT]
                        );
                      }
                      if (values[TOP_DOWN_STEP]) {
                        isValid = !checkIsDateAfterPrevious(
                          startDate,
                          values[TOP_DOWN_STEP][START_AT]
                        );
                      }
                      if (values[DOWN_UP_STEP]) {
                        isValid = !checkIsDateAfterPrevious(
                          startDate,
                          values[DOWN_UP_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[EMAIL_STEP]
          ? [
              {
                name: EMAIL_SUBJECT,
                type: TEXT,
                fieldWrapperClass: classes.subject,
                required: true,
                translationKey: 'subject',
                isToggleable: true,
                shouldFadeOutInstant: true,
                fieldGroup: EMAIL_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === EMAIL_STEP,
                validators: [
                  { type: 'required', validator: isEmpty },
                  {
                    type: 'forbiddenCharacters',
                    validator: forbiddenCharacters,
                  },
                  {
                    type: 'maxLength',
                    validator: value => validateLength(value, 0, 150),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[EMAIL_STEP]
          ? [
              {
                name: EMAIL_MESSAGE,
                type: TEXT,
                translationKey: 'emailMessage',
                fieldWrapperClass: classes.message,
                multiline: true,
                rows: 4,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: EMAIL_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === EMAIL_STEP,
                validators: [
                  { type: 'required', validator: isEmpty },
                  {
                    type: 'forbiddenCharacters',
                    validator: forbiddenCharacters,
                  },
                  {
                    type: 'emailContentMaxLength',
                    validator: value => validateLength(value, 0, 500),
                  },
                ],
              },
            ]
          : []),
        // SELF REVIEW STEP FIELDS
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: 'selfReviewStepSubtitle',
                type: SUBTITLE,
                fieldWrapperClass: classes.subtitle,
                isToggleable: true,
                shouldFadeOutInstant: true,
                variant: 'h4',
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: START_AT,
                type: DATE_PICKER,
                fieldWrapperClass: classes.startDate,
                translationKey: 'stepStartDate',
                isToolbarDisabled: true,
                isPastDisabled: true,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: SELF_REVIEW_STEP,
                isExternalyManagedValue: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
                dependants: [
                  {
                    name: END_AT,
                    manualResetFn: ({
                      currentFieldValue,
                      setFieldValue,
                      values,
                    }) =>
                      setFieldValue(
                        SELF_REVIEW_STEP,
                        {
                          ...values[SELF_REVIEW_STEP],
                          [START_AT]: currentFieldValue,
                          [END_AT]: addNumberOfDays(currentFieldValue, 2),
                        },
                        false
                      ),
                  },
                ],
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                  {
                    type: 'stepStartDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[EMAIL_STEP]) {
                        isValid = checkIsDateAfterPrevious(
                          startDate,
                          values[EMAIL_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: START_AT,
                type: TIME_PICKER,
                fieldWrapperClass: classes.startTime,
                isToggleable: true,
                shouldHideLabel: true,
                shouldShowError: false,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: SELF_REVIEW_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                  {
                    type: 'stepStartDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[EMAIL_STEP]) {
                        isValid = checkIsDateAfterPrevious(
                          startDate,
                          values[EMAIL_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: END_AT,
                type: DATE_PICKER,
                fieldWrapperClass: classes.endDate,
                translationKey: 'stepEndDate',
                isToolbarDisabled: true,
                isPastDisabled: true,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: SELF_REVIEW_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
                getMinDate: (value, values) => {
                  return addNumberOfDays(
                    new Date(values[SELF_REVIEW_STEP][START_AT]),
                    2
                  );
                },
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: END_AT,
                type: TIME_PICKER,
                fieldWrapperClass: classes.endTime,
                customRootClass: classes.timePicker,
                isToggleable: true,
                shouldHideLabel: true,
                shouldShowError: false,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: SELF_REVIEW_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: HAS_EMAIL,
                type: SWITCH,
                translationKey: 'stepEmail',
                fieldWrapperClass: classes.stepEmail,
                isHighlightedLabel: true,
                fieldGroup: SELF_REVIEW_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: EMAIL_MESSAGE,
                type: TEXT,
                translationKey: 'stepEmailContent',
                fieldWrapperClass: classes.stepEmailContent,
                multiline: true,
                rows: 4,
                fieldGroup: SELF_REVIEW_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                required: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP &&
                  values[SELF_REVIEW_STEP]?.[HAS_EMAIL],
                validators: [
                  { type: 'required', validator: isEmpty },
                  {
                    type: 'forbiddenCharacters',
                    validator: forbiddenCharacters,
                  },
                  {
                    type: 'emailContentMaxLength',
                    validator: value => validateLength(value, 0, 500),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: ACTIVE_SETTINGS,
                type: SETTINGS_SUMMARY,
                translationKey: 'attributesSettings',
                fieldWrapperClass: classes.attributes,
                icon: AttributesIcon,
                settingsKey: ATTRIBUTES,
                isToggleable: true,
                shouldFadeOutInstant: true,
                settingsGroup: SELF_REVIEW_STEP,
                isSubSetting: true,
                prepareValueCount: value => value.length,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: ACTIVE_SETTINGS,
                type: SETTINGS_SUMMARY,
                translationKey: 'participantsSettings',
                fieldWrapperClass: classes.participants,
                icon: UsersIcon,
                settingsKey: ASSIGNMENTS,
                isSubSetting: true,
                settingsGroup: SELF_REVIEW_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                prepareValueCount: value =>
                  getParticipantsCount(SELF_REVIEW_STEP, value),
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP,
              },
            ]
          : []),
        // TOP > DOWN STEP FIELDS
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: 'topDownStepSubtitle',
                type: SUBTITLE,
                fieldWrapperClass: classes.subtitle,
                isToggleable: true,
                shouldFadeOutInstant: true,
                variant: 'h4',
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: START_AT,
                type: DATE_PICKER,
                fieldWrapperClass: classes.startDate,
                translationKey: 'stepStartDate',
                isToolbarDisabled: true,
                isPastDisabled: true,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: TOP_DOWN_STEP,
                isExternalyManagedValue: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
                dependants: [
                  {
                    name: END_AT,
                    manualResetFn: ({
                      currentFieldValue,
                      setFieldValue,
                      values,
                    }) =>
                      setFieldValue(
                        TOP_DOWN_STEP,
                        {
                          ...values[TOP_DOWN_STEP],
                          [START_AT]: currentFieldValue,
                          [END_AT]: addNumberOfDays(currentFieldValue, 2),
                        },
                        false
                      ),
                  },
                ],
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                  {
                    type: 'stepStartDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[EMAIL_STEP]) {
                        isValid = checkIsDateAfterPrevious(
                          startDate,
                          values[EMAIL_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: START_AT,
                type: TIME_PICKER,
                fieldWrapperClass: classes.startTime,
                isToggleable: true,
                shouldHideLabel: true,
                shouldShowError: false,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: TOP_DOWN_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                  {
                    type: 'stepStartDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[EMAIL_STEP]) {
                        isValid = checkIsDateAfterPrevious(
                          startDate,
                          values[EMAIL_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: END_AT,
                type: DATE_PICKER,
                fieldWrapperClass: classes.endDate,
                translationKey: 'stepEndDate',
                isToolbarDisabled: true,
                isPastDisabled: true,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: TOP_DOWN_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
                getMinDate: (value, values) => {
                  return addNumberOfDays(
                    new Date(values[TOP_DOWN_STEP][START_AT]),
                    2
                  );
                },
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: END_AT,
                type: TIME_PICKER,
                fieldWrapperClass: classes.endTime,
                customRootClass: classes.timePicker,
                isToggleable: true,
                shouldHideLabel: true,
                shouldShowError: false,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: TOP_DOWN_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: HAS_EMAIL,
                type: SWITCH,
                translationKey: 'stepEmail',
                fieldWrapperClass: classes.stepEmail,
                isHighlightedLabel: true,
                fieldGroup: TOP_DOWN_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: EMAIL_MESSAGE,
                type: TEXT,
                translationKey: 'stepEmailContent',
                fieldWrapperClass: classes.stepEmailContent,
                multiline: true,
                rows: 4,
                fieldGroup: TOP_DOWN_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                required: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP &&
                  values[TOP_DOWN_STEP]?.[HAS_EMAIL],
                validators: [
                  { type: 'required', validator: isEmpty },
                  {
                    type: 'forbiddenCharacters',
                    validator: forbiddenCharacters,
                  },
                  {
                    type: 'emailContentMaxLength',
                    validator: value => validateLength(value, 0, 500),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: ACTIVE_SETTINGS,
                type: SETTINGS_SUMMARY,
                translationKey: 'attributesSettings',
                fieldWrapperClass: classes.attributes,
                icon: AttributesIcon,
                settingsKey: ATTRIBUTES,
                isToggleable: true,
                shouldFadeOutInstant: true,
                isSubSetting: true,
                settingsGroup: TOP_DOWN_STEP,
                prepareValueCount: value => value.length,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: ACTIVE_SETTINGS,
                type: SETTINGS_SUMMARY,
                translationKey: 'participantsSettings',
                fieldWrapperClass: classes.participants,
                icon: UsersIcon,
                settingsKey: ASSIGNMENTS,
                isSubSetting: true,
                settingsGroup: TOP_DOWN_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                prepareValueCount: value =>
                  getParticipantsCount(TOP_DOWN_STEP, value),
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP,
              },
            ]
          : []),
        // DOWN > UP STEP FIELDS
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: 'downUpStepSubtitle',
                type: SUBTITLE,
                fieldWrapperClass: classes.subtitle,
                isToggleable: true,
                shouldFadeOutInstant: true,
                variant: 'h4',
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: START_AT,
                type: DATE_PICKER,
                fieldWrapperClass: classes.startDate,
                translationKey: 'stepStartDate',
                isToolbarDisabled: true,
                isPastDisabled: true,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: DOWN_UP_STEP,
                isExternalyManagedValue: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
                dependants: [
                  {
                    name: END_AT,
                    manualResetFn: ({
                      currentFieldValue,
                      setFieldValue,
                      values,
                    }) =>
                      setFieldValue(
                        DOWN_UP_STEP,
                        {
                          ...values[DOWN_UP_STEP],
                          [START_AT]: currentFieldValue,
                          [END_AT]: addNumberOfDays(currentFieldValue, 2),
                        },
                        false
                      ),
                  },
                ],
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                  {
                    type: 'stepStartDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[EMAIL_STEP]) {
                        isValid = checkIsDateAfterPrevious(
                          startDate,
                          values[EMAIL_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: START_AT,
                type: TIME_PICKER,
                fieldWrapperClass: classes.startTime,
                isToggleable: true,
                shouldHideLabel: true,
                shouldShowError: false,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: DOWN_UP_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                  {
                    type: 'stepStartDateInvalid',
                    shouldPassFormValues: true,
                    validator: (startDate, values) => {
                      let isValid = true;

                      if (values[EMAIL_STEP]) {
                        isValid = checkIsDateAfterPrevious(
                          startDate,
                          values[EMAIL_STEP][START_AT]
                        );
                      }

                      return isValid;
                    },
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: END_AT,
                type: DATE_PICKER,
                fieldWrapperClass: classes.endDate,
                translationKey: 'stepEndDate',
                isToolbarDisabled: true,
                isPastDisabled: true,
                isToggleable: true,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: DOWN_UP_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
                getMinDate: (value, values) => {
                  return addNumberOfDays(
                    new Date(values[DOWN_UP_STEP][START_AT]),
                    2
                  );
                },
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: END_AT,
                type: TIME_PICKER,
                fieldWrapperClass: classes.endTime,
                customRootClass: classes.timePicker,
                isToggleable: true,
                shouldHideLabel: true,
                shouldShowError: false,
                required: true,
                shouldFadeOutInstant: true,
                fieldGroup: DOWN_UP_STEP,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
                validators: [
                  {
                    type: 'dateInPast',
                    validator: date => !isDateInPast(date),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: HAS_EMAIL,
                type: SWITCH,
                translationKey: 'stepEmail',
                fieldWrapperClass: classes.stepEmail,
                isHighlightedLabel: true,
                fieldGroup: DOWN_UP_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: EMAIL_MESSAGE,
                type: TEXT,
                translationKey: 'stepEmailContent',
                fieldWrapperClass: classes.stepEmailContent,
                multiline: true,
                rows: 4,
                fieldGroup: DOWN_UP_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                required: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP &&
                  values[DOWN_UP_STEP]?.[HAS_EMAIL],
                validators: [
                  { type: 'required', validator: isEmpty },
                  {
                    type: 'forbiddenCharacters',
                    validator: forbiddenCharacters,
                  },
                  {
                    type: 'emailContentMaxLength',
                    validator: value => validateLength(value, 0, 500),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: ACTIVE_SETTINGS,
                type: SETTINGS_SUMMARY,
                translationKey: 'attributesSettings',
                fieldWrapperClass: classes.attributes,
                icon: AttributesIcon,
                settingsKey: ATTRIBUTES,
                isToggleable: true,
                shouldFadeOutInstant: true,
                isSubSetting: true,
                settingsGroup: DOWN_UP_STEP,
                prepareValueCount: value => value.length,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: ACTIVE_SETTINGS,
                type: SETTINGS_SUMMARY,
                translationKey: 'participantsSettings',
                fieldWrapperClass: classes.participants,
                icon: UsersIcon,
                settingsKey: ASSIGNMENTS,
                isSubSetting: true,
                settingsGroup: DOWN_UP_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                prepareValueCount: value =>
                  getParticipantsCount(DOWN_UP_STEP, value),
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP,
              },
            ]
          : []),
      ],
    },
    {
      columnContentWrapperClass: 'columnContent',
      id: 3,

      fields: [
        // EMPLOYEES IN STEP
        {
          name: 'employeesInCycle',
          type: SUBTITLE,
          fieldWrapperClass: classes.subtitle,
          isToggleable: true,
          shouldFadeOutInstant: true,
          variant: 'h4',
          shouldRender: values => values?.[ACTIVE_SETTINGS]?.main === USERS,
        },
        {
          name: USERS,
          type: MANAGE_USERS_LIST,
          shouldRemoveFieldSpacing: true,
          showSelection: true,
          shouldCalculateSelectionHeight: true,
          isToggleable: true,
          shouldRender: values => values?.[ACTIVE_SETTINGS]?.main === USERS,
        },
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: ATTRIBUTES,
                type: SELECT,
                labelClass: classes.attributesLabel,
                optionsKey: 'attributes',
                required: true,
                isAttribute: true,
                hasAttributeComments: true,
                shouldReturnOption: true,
                shouldDisableSort: true,
                isMulti: true,
                isAttributeDraggable: true,
                hasAttributePreview: true,
                hasColorBox: true,
                addCommentsFieldName: 'has_answer_comment',
                fieldGroup: SELF_REVIEW_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                onAttributePreview,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP &&
                  values?.[ACTIVE_SETTINGS]?.sub === ATTRIBUTES,
                parser: {
                  value: 'id',
                  label: 'name',
                },
                validators: [
                  {
                    type: 'required',
                    validator: value => !isArrayEmpty(value),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: ATTRIBUTES,
                type: SELECT,
                labelClass: classes.attributesLabel,
                optionsKey: 'attributes',
                required: true,
                isAttribute: true,
                hasAttributeComments: true,
                hasAttributePreview: true,
                shouldReturnOption: true,
                shouldDisableSort: true,
                isMulti: true,
                isAttributeDraggable: true,
                hasColorBox: true,
                addCommentsFieldName: 'has_answer_comment',
                fieldGroup: TOP_DOWN_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                onAttributePreview,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP &&
                  values?.[ACTIVE_SETTINGS]?.sub === ATTRIBUTES,
                parser: {
                  value: 'id',
                  label: 'name',
                },
                validators: [
                  {
                    type: 'required',
                    validator: value => !isArrayEmpty(value),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: ATTRIBUTES,
                type: SELECT,
                labelClass: classes.attributesLabel,
                optionsKey: 'attributes',
                required: true,
                isAttribute: true,
                hasAttributeComments: true,
                hasAttributePreview: true,
                shouldReturnOption: true,
                shouldDisableSort: true,
                isMulti: true,
                isAttributeDraggable: true,
                hasColorBox: true,
                addCommentsFieldName: 'has_answer_comment',
                fieldGroup: DOWN_UP_STEP,
                isToggleable: true,
                shouldFadeOutInstant: true,
                onAttributePreview,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP &&
                  values?.[ACTIVE_SETTINGS]?.sub === ATTRIBUTES,
                parser: {
                  value: 'id',
                  label: 'name',
                },
                validators: [
                  {
                    type: 'required',
                    validator: value => !isArrayEmpty(value),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[SELF_REVIEW_STEP]
          ? [
              {
                name: ASSIGNMENTS,
                type: CYCLE_STEP_ASSIGNMENTS,
                required: true,
                fieldGroup: SELF_REVIEW_STEP,
                shouldFadeOutInstant: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === SELF_REVIEW_STEP &&
                  values?.[ACTIVE_SETTINGS]?.sub === ASSIGNMENTS,
                validators: [
                  {
                    type: 'required',
                    validator: value => !isArrayEmpty(value),
                  },
                  {
                    type: 'invalidReviewStructure',
                    validator: value =>
                      value.some(assignment =>
                        assignment[REVIEWERS].some(user => !user.isExcluded)
                      ),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[TOP_DOWN_STEP]
          ? [
              {
                name: ASSIGNMENTS,
                type: CYCLE_STEP_ASSIGNMENTS,
                required: true,
                fieldGroup: TOP_DOWN_STEP,
                shouldFadeOutInstant: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === TOP_DOWN_STEP &&
                  values?.[ACTIVE_SETTINGS]?.sub === ASSIGNMENTS,
                validators: [
                  {
                    type: 'required',
                    validator: value => !isArrayEmpty(value),
                  },
                  {
                    type: 'invalidReviewStructure',
                    validator: value =>
                      value.some(
                        assignment =>
                          assignment[REVIEWERS].some(
                            user => !user.isExcluded
                          ) &&
                          assignment[CREATED_FOR].some(user => !user.isExcluded)
                      ),
                  },
                ],
              },
            ]
          : []),
        ...(activeSteps[DOWN_UP_STEP]
          ? [
              {
                name: ASSIGNMENTS,
                type: CYCLE_STEP_ASSIGNMENTS,
                required: true,
                fieldGroup: DOWN_UP_STEP,
                shouldFadeOutInstant: true,
                shouldRender: values =>
                  values?.[ACTIVE_SETTINGS]?.main === DOWN_UP_STEP &&
                  values?.[ACTIVE_SETTINGS]?.sub === ASSIGNMENTS,
                validators: [
                  {
                    type: 'required',
                    validator: value => !isArrayEmpty(value),
                  },
                  {
                    type: 'invalidReviewStructure',
                    validator: value =>
                      value.some(
                        assignment =>
                          assignment[REVIEWERS].some(
                            user => !user.isExcluded
                          ) &&
                          assignment[CREATED_FOR].some(user => !user.isExcluded)
                      ),
                  },
                ],
              },
            ]
          : []),
      ],
    },
  ];
};

export const prepareCycleHeaderActions = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    rowCellClass: 'actions-cell',
    align: 'right',
    isHeaderAction: true,
    minWidth: 60,
    maxWidth: 125,
  },
];

export const prepareCycleTableHeaders = labels => [
  {
    id: 1,
    title: labels.title,
    rowKey: 'title',
    sortAs: 'title',
    isHighlighted: true,
    rowCellClass: 'title-cell',
    minWidth: 180,
    maxWidth: '1fr',
    hasConditionalTooltip: true,
  },
  {
    id: 2,
    title: labels.startsOn,
    rowKey: 'startsOn',
    sortAs: 'start_at',
    rowCellClass: 'start-at-cell',
    minWidth: 120,
    maxWidth: 135,
  },
  {
    id: 3,
    title: labels.endsOn,
    rowKey: 'endsOn',
    sortAs: 'end_at',
    rowCellClass: 'end-at-cell',
    minWidth: 120,
    maxWidth: 135,
  },
  {
    id: 4,
    title: labels.timeline,
    rowKey: 'timeline',
    rowCellClass: 'timeline-cell',
    hasConditionalTooltip: true,
    isSortingDisabled: true,
    minWidth: 145,
    maxWidth: '0.5fr',
  },
  {
    id: 5,
    title: labels.employeesInCycle,
    rowKey: 'employeesInCycle',
    rowCellClass: 'cycle-users-cell',
    isSortingDisabled: true,
    minWidth: 120,
    maxWidth: '0.5fr',
  },
  {
    id: 6,
    title: labels.status,
    rowKey: 'status',
    sortAs: 'status',
    minWidth: 110,
    maxWidth: '0.5fr',
  },
  {
    id: 7,
    title: labels.repeat,
    rowKey: 'repeat',
    isSortingDisabled: true,
    minWidth: 130,
    maxWidth: '0.5fr',
  },
];

export const prepareCycleTableData = (
  labels,
  canManage,
  cycles,
  statuses,
  intervalOptions,
  renderStatus,
  renderActions
) => {
  const { IS_REPEATING, REPEAT_INTERVAL_UNIT, REPEAT_INTERVAL } =
    MANAGE_CYCLE_FIELD_NAMES;
  if (isArrayEmpty(cycles)) return [];

  return cycles.map(cycle => {
    const { start_at, end_at, status, users_count } = cycle;
    const cycleStatus = getItemById(statuses, status);
    const detailsUrl = `/cycles/${cycle.id}/details`;

    return {
      ...cycle,
      reviews: cycle?.reviews || [],
      [IS_REPEATING]: cycle[IS_REPEATING]
        ? `${labels.after} ${
            getItemById(intervalOptions, cycle[REPEAT_INTERVAL_UNIT])
              ?.periods?.[cycle[REPEAT_INTERVAL] - 1]?.name
          }`
        : '-',
      startsOn: formatDate(start_at),
      endsOn: formatDate(end_at),
      timeline: prepareTableTimeline(
        labels.timeline,
        start_at,
        end_at,
        status === CYCLE_STATUSES.DONE
      ),
      status: renderStatus(cycleStatus),
      employeesInCycle: users_count,
      ...(canManage ? { actions: renderActions(cycle) } : {}),
      isDisabled: status === CYCLE_STATUSES.PENDING,
      cellLinks: {
        title: {
          to: detailsUrl,
        },
        startsOn: {
          to: detailsUrl,
        },
        endsOn: {
          to: detailsUrl,
        },
        timeline: {
          to: detailsUrl,
        },
        employeesInCycle: {
          to: detailsUrl,
        },
        status: {
          to: detailsUrl,
        },
        repeat: {
          to: detailsUrl,
        },
      },
    };
  });
};

export const prepareCycleReviewTableHeaderActions = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    rowCellClass: 'actions-cell',
    align: 'right',
    isHeaderAction: true,
    minWidth: 60,
    maxWidth: 90,
  },
];

export const prepareCycleReviewTableHeaders = labels => [
  {
    id: 1,
    title: labels.type,
    rowKey: 'type',
    sortAs: 'type',
    minWidth: 50,
    maxWidth: 50,
  },
  {
    id: 2,
    title: labels.title,
    rowKey: 'title',
    isHighlighted: true,
    minWidth: 140,
    maxWidth: '1.5fr',
  },
  {
    id: 3,
    title: labels.startsOn,
    rowKey: 'startsOn',
    minWidth: 90,
    maxWidth: '1fr',
  },
  {
    id: 4,
    title: labels.endsOn,
    rowKey: 'endsOn',
    minWidth: 90,
    maxWidth: '1fr',
  },
  {
    id: 5,
    title: labels.status,
    rowKey: 'status',
    rowCellClass: 'status-cell',
    minWidth: 160,
    maxWidth: '1fr',
  },
];

export const prepareCycleReviewsData = (
  reviews,
  translations,
  reviewTypes,
  renderType,
  renderTitle,
  renderStatus,
  renderActions
) => {
  const DATE_FORMAT = 'dd MMM y, hh:mm a';
  const translatedStatuses = prepareSurveyStatuses(translations.statuses);

  return reviews.map(review => {
    const type = getItemById(reviewTypes, review.type);
    const status = getItemById(translatedStatuses, review.status);

    return {
      id: review.id,
      creatorId: review.creator,
      type: renderType(type),
      title: renderTitle(review),
      startsOn: formatDate(review.sent_at || review.scheduled_for, DATE_FORMAT),
      endsOn: formatDate(review.expire_at, DATE_FORMAT),
      status: renderStatus(review, status, type),
      actions: renderActions(review),
    };
  });
};

export const prepareCycleStatuses = labels =>
  CYCLES_STATUSES_LIST.map(asu => ({
    ...asu,
    name: labels[asu.key],
    label: labels[asu.key],
  }));

export const prepareCyclesPageFilters = (
  translations,
  statuses,
  allEmployees = []
) => {
  const { STATUS, CREATOR } = PARAMS;
  const users = prepareFilterableUsers(allEmployees);
  const defaultOption = { id: null, name: translations.all };
  return [
    {
      id: STATUS,
      name: translations.filters.labels.status,
      icon: StatusIcon,
      isSingleSelect: true,
      items: [defaultOption, ...statuses],
    },
    {
      id: CREATOR,
      name: translations.filters.labels[CREATOR],
      icon: CreatorIcon,
      items: users,
    },
  ];
};

export const prepareCycleReviewMenuItems = (
  labels,
  survey,
  onSendReminder = () => {},
  onChangeSurveyTitle = () => {}
) => [
  {
    type: 'reminder',
    label: labels.reminder,
    shouldItemRender: () => survey.status === SURVEY_STATUS_TYPES.ACTIVE,
    action: () => onSendReminder(survey.id),
    icon: ReminderIcon,
  },
  {
    type: 'edit',
    label: labels.rename,
    shouldItemRender: () => true,
    action: () => onChangeSurveyTitle(survey),
    icon: SurveyTitleIcon,
  },
];

export const prepareCycleStepsTimelineAndStats = (
  labels,
  cycleSteps,
  steps
) => {
  const START_DATE_FORMAT = 'LLL dd, hh:mm a';
  const END_DATE_FORMAT = 'LLL dd yyyy, hh:mm a';

  if (!cycleSteps) return {};

  const emailStepIndex = getItemIndex(
    cycleSteps,
    CYCLE_STEP_TYPES.EMAIL_STEP,
    'type'
  );

  const timelineStats = cycleSteps.map((cycleStep, index) => {
    const currentStep = getItemById(steps, cycleStep.type);
    const isActive = isDateInPast(cycleStep[STEP_GLOBAL_FIELDS.START_AT]);
    const isEmailStep = emailStepIndex === index;
    const totalEmails = cycleStep.stats?.total_step_users || 0;
    const totalUsers = cycleStep.stats?.reviewers_count || 0;
    const completedUsers = cycleStep.stats?.done_reviewers_count || 0;
    const percentage = calculatePercentageToNDecimals(
      completedUsers,
      totalUsers
    );

    return {
      ...cycleStep,
      name: currentStep.name,
      icon: currentStep.icon,
      isActive,
      isEmailStep,
      startsAt: formatDate(
        cycleStep[STEP_GLOBAL_FIELDS.START_AT],
        START_DATE_FORMAT
      ),
      ...(!isEmailStep
        ? {
            endsAt: formatDate(
              cycleStep[STEP_GLOBAL_FIELDS.END_AT],
              END_DATE_FORMAT
            ),
          }
        : {}),
      stats: {
        ...(index === emailStepIndex
          ? {
              label: isActive ? labels.sent : labels.scheduled,
              totalUsers: totalEmails,
            }
          : {}),
        ...(index !== emailStepIndex
          ? {
              label: labels.completed,
              totalUsers,
              completedUsers,
              percentage,
            }
          : {}),
      },
    };
  });

  return {
    timelineStats,
    hasEmailStep: emailStepIndex !== -1,
  };
};

export const prepareEmployeesTableHeaderActions = labels => [
  {
    id: 'actions-column',
    title: labels.actions,
    rowKey: 'actions',
    rowCellClass: 'actions-cell',
    align: 'right',
    isHeaderAction: true,
    minWidth: 65,
    maxWidth: 65,
  },
];

export const prepareEmployeesTableHeaders = labels => [
  {
    id: 1,
    title: labels.name,
    rowKey: 'user',
    minWidth: 250,
    maxWidth: '1.5fr',
    hasConditionalTooltip: true,
  },
  {
    id: 2,
    title: labels.self,
    rowKey: 'self',
    isHighlighted: true,
    maxWidth: '1fr',
  },
  {
    id: 3,
    title: labels.topDown,
    rowKey: 'topDown',
    isHighlighted: true,
    maxWidth: '1fr',
  },
  {
    id: 4,
    title: labels.downUp,
    rowKey: 'downUp',
    isHighlighted: true,
    maxWidth: '1fr',
  },
];

export const prepareEmployeesTableData = (
  users,
  status,
  currentUser,
  accessControl,
  renderEmployee,
  renderActions
) => {
  if (!users || isArrayEmpty(users)) return [];

  return users.map(user => {
    const canAccessUserProfile = isUserProfileAccessibleCheck(
      accessControl,
      currentUser.id,
      user.id
    );
    return {
      ...user,
      user: renderEmployee(user),
      self:
        user.self_total_reviews === 0
          ? '-'
          : `${user.self_submitted_reviews} / ${user.self_total_reviews}`,
      topDown:
        user.top_down_total_reviews === 0
          ? '-'
          : `${user.top_down_submitted_reviews} / ${user.top_down_total_reviews}`,
      downUp:
        user.down_up_total_reviews === 0
          ? '-'
          : `${user.down_up_submitted_reviews} / ${user.down_up_total_reviews}`,
      actions: renderActions(
        user,
        canAccessUserProfile && status === CYCLE_STATUSES.DONE
      ),
    };
  });
};

export const prepareCycleAttributes = (
  labels,
  attributes,
  attributesToCompare = []
) => {
  const { SELF_REVIEW_STEP, TOP_DOWN_STEP, DOWN_UP_STEP } = CYCLE_STEP_TYPES;
  const attributesCount = attributes.length;
  if (isArrayEmpty(attributes)) return [];

  const uniqueAttributesToCompare = !isArrayEmpty(attributesToCompare)
    ? getArrayDifference(attributesToCompare, attributes)
    : [];

  return [...attributes, ...uniqueAttributesToCompare].reduce(
    (acc, attribute, index) => {
      const isReviewFromLastCycle = index <= attributesCount - 1;

      return [
        ...acc,
        {
          ...attribute,
          isTextReply: attribute.text_reply_questions,
          reviews: [
            {
              id: SELF_REVIEW_STEP,
              name: labels.self,
              score:
                (isReviewFromLastCycle &&
                  attribute.scores?.[SELF_REVIEW_STEP]) ||
                null,
              hasReplies:
                isReviewFromLastCycle && attribute.replies?.[SELF_REVIEW_STEP],
            },
            {
              id: TOP_DOWN_STEP,
              name: labels.topDown,
              score:
                (isReviewFromLastCycle && attribute.scores?.[TOP_DOWN_STEP]) ||
                null,
              hasReplies:
                isReviewFromLastCycle && attribute.replies?.[TOP_DOWN_STEP],
            },
            {
              id: DOWN_UP_STEP,
              name: labels.downUp,
              score:
                (isReviewFromLastCycle && attribute.scores?.[DOWN_UP_STEP]) ||
                null,
              hasReplies:
                isReviewFromLastCycle && attribute.replies?.[DOWN_UP_STEP],
            },
          ],
        },
      ];
    },
    []
  );
};

export const prepareCycleAttributesForCompare = (
  labels,
  attributes,
  attributesToCompare
) => {
  if (isArrayEmpty(attributesToCompare) || isArrayEmpty(attributes)) return [];
  const uniqueAttributesToCompare = !isArrayEmpty(attributesToCompare)
    ? getArrayDifference(attributesToCompare, attributes)
    : [];
  const preparedAttributesToCompare = prepareCycleAttributes(
    labels,
    uniqueAttributesToCompare
  );

  const mutualAttributes = attributes.reduce((acc, attribute) => {
    const mutualAttribute = getItemById(attributesToCompare, attribute.id);

    return [
      ...acc,
      {
        ...attribute,
        isTextReply: attribute.text_reply_questions,
        reviews: [
          {
            id: CYCLE_STEP_TYPES.SELF_REVIEW_STEP,
            name: labels.self,
            score:
              mutualAttribute?.scores?.[CYCLE_STEP_TYPES.SELF_REVIEW_STEP] ||
              null,
          },
          {
            id: CYCLE_STEP_TYPES.TOP_DOWN_STEP,
            name: labels.topDown,
            score:
              mutualAttribute?.scores?.[CYCLE_STEP_TYPES.TOP_DOWN_STEP] || null,
          },
          {
            id: CYCLE_STEP_TYPES.DOWN_UP_STEP,
            name: labels.downUp,
            score:
              mutualAttribute?.scores?.[CYCLE_STEP_TYPES.DOWN_UP_STEP] || null,
          },
        ],
      },
    ];
  }, []);

  return [...mutualAttributes, ...preparedAttributesToCompare];
};

export const prepareEmployeeReviewAnswers = reviewAnswers => {
  if (!reviewAnswers) return [];

  return Object.keys(reviewAnswers).reduce((acc, questionKey) => {
    return [...acc, { ...reviewAnswers[questionKey] }];
  }, []);
};
